import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Icon,
  IntentBadge,
} from "@excellent/ui";
import { RouterLink as Link } from "@excellent/ui/router";
import {
  type AgentImprovementDashboardResponse,
} from "@excellent/ui/api/contracts/agent-improvement-dashboard";

interface AgentImprovementOverviewCardProps {
  data?: AgentImprovementDashboardResponse;
  fallbackActorId?: string;
  fallbackEnvironment?: "demo" | "real";
  isLoading?: boolean;
  error?: unknown;
}

type ClaimSummaryItem = AgentImprovementDashboardResponse["claimSummary"]["claim"];
type OverviewStoryRow = {
  label: string;
  value: string;
  status: string;
  intent: "success" | "info" | "warning" | "neutral";
  detail?: string;
};

function connectionIntent(state: string): "success" | "info" | "warning" | "neutral" {
  if (state === "connected") return "success";
  if (state === "observed" || state === "configured") return "info";
  if (state === "unknown") return "warning";
  return "neutral";
}

function intentForState(state: ClaimSummaryItem["state"]): OverviewStoryRow["intent"] {
  if (["claim_ready", "ready", "delivered", "applied", "recommended", "active", "projected", "clear"].includes(state)) return "success";
  if (["tracked", "observed_only", "configured", "not_counted"].includes(state)) return "info";
  if (["required", "unavailable", "not_claimable", "gated", "not_inspected"].includes(state)) return "warning";
  return "neutral";
}

function statusForState(state: ClaimSummaryItem["state"] | string): string {
  return state.replace(/_/g, " ");
}

function withoutMillLanguage(text: string): string {
  if (!/\bsmoke[\s-]?pilot\b|\binheritance mechanism\b/i.test(text)) return text;
  const honestTail = text.match(/(?:That|It) does not prove[^.]*\./i)?.[0];
  return honestTail
    ? `Excellent recorded an attempt on this host. ${honestTail}`
    : "Excellent recorded an attempt on this host. That does not prove the agent improved.";
}

function honestNextAction(action: string): string {
  if (/\bsmoke[\s-]?pilot\b|\binheritance mechanism\b/i.test(action)) {
    return "Request independent review before any improvement claim.";
  }
  return action;
}

function firstExposureId(text: string | null | undefined): string | null {
  const match = text?.match(/exposure:[a-z0-9-]+/i);
  return match?.[0] ?? null;
}

function humanMissReason(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const stripped = withoutMillLanguage(raw.replace(/^refusal:\s*/i, "").trim())
    .replace(/; this records the current-host miss and why\.?$/i, "")
    .trim();
  if (!stripped) return null;
  if (/^(exposure:)?failed$/i.test(stripped)) return "This attempt missed.";
  return stripped;
}

function summaryItemRow(item: ClaimSummaryItem): OverviewStoryRow {
  return {
    label: item.label,
    value: item.value,
    status: statusForState(item.state),
    intent: intentForState(item.state),
    detail: withoutMillLanguage(item.detail),
  };
}

function overviewLoopStoryRows(data: AgentImprovementDashboardResponse | undefined, error?: unknown): OverviewStoryRow[] {
  if (error && !data) {
    return [
      { label: "Record", value: "Not inspected", status: "unavailable", intent: "warning" },
      { label: "Miss", value: "Not inspected", status: "unknown", intent: "neutral" },
      { label: "Why", value: "Not inspected", status: "unknown", intent: "neutral" },
      { label: "Next", value: "Not inspected", status: "unknown", intent: "neutral" },
    ];
  }
  if (!data) {
    return [
      { label: "Record", value: "Checking product evidence", status: "checking", intent: "neutral" },
      { label: "Miss", value: "Checking", status: "checking", intent: "neutral" },
      { label: "Why", value: "Checking", status: "checking", intent: "neutral" },
      { label: "Next", value: "Checking", status: "checking", intent: "neutral" },
    ];
  }

  const summary = data.claimSummary;
  const missIdFromSummary = firstExposureId(summary.learned.detail);
  const missExposure = (missIdFromSummary
    ? data.learningExposures.find((exposure) => exposure.id === missIdFromSummary)
    : null)
    ?? data.learningExposures.find((exposure) => exposure.status === "failed" && summary.learned.value.includes(exposure.learningId))
    ?? null;
  const missWork = data.measurementPilot?.workItems.find((item) =>
    item.arm === "baseline" &&
    item.verdict === "FAIL" &&
    Boolean(item.attrition) &&
    (missIdFromSummary ? item.id.includes(missIdFromSummary) : true),
  ) ?? null;
  const missReason = humanMissReason(
    missWork?.attrition
    ?? missExposure?.failureEvidence?.message
    ?? (/\bmiss\b/i.test(summary.learned.detail) ? summary.learned.detail : null),
  );
  const missId = missIdFromSummary ?? missExposure?.id ?? null;
  const appliedId = firstExposureId(summary.handoff.detail) ?? firstExposureId(summary.application.detail);
  const nextAction = honestNextAction(summary.nextAction);
  const laterAttempt = summary.application.state === "applied"
    ? appliedId
      ? `Later attempt ${appliedId}`
      : summary.application.value
    : nextAction;

  return [
    {
      label: "Record",
      value: "Recorded on this host",
      status: statusForState(summary.claim.state),
      intent: intentForState(summary.claim.state),
      detail: withoutMillLanguage(summary.claim.detail),
    },
    {
      label: "Miss",
      value: missReason ?? "No miss recorded",
      status: missReason ? "miss" : "none",
      intent: missReason ? "warning" : "neutral",
      detail: missReason && missId
        ? `Miss ${missId}.`
        : withoutMillLanguage(summary.learned.detail),
    },
    {
      label: "Why",
      value: summary.learned.value,
      status: statusForState(summary.learned.state),
      intent: intentForState(summary.learned.state),
      detail: withoutMillLanguage(summary.learned.detail),
    },
    {
      label: "Next",
      value: laterAttempt,
      status: statusForState(summary.application.state),
      intent: intentForState(summary.application.state),
      detail: [
        summary.application.state === "applied"
          ? withoutMillLanguage(summary.application.detail)
          : "No later attempt is visible yet.",
        appliedId && summary.application.state === "applied" ? `Applied ${appliedId}.` : null,
        nextAction,
      ].filter(Boolean).join(" "),
    },
  ];
}

function overviewEvidencePathRows(data: AgentImprovementDashboardResponse | undefined, error?: unknown): OverviewStoryRow[] {
  if (error && !data) {
    return [
      {
        label: "Evidence source",
        value: "Dashboard API unavailable",
        status: "unavailable",
        intent: "warning",
      },
      {
        label: "Codex integration",
        value: "Not inspected",
        status: "unknown",
        intent: "neutral",
      },
      {
        label: "Learning delivered",
        value: "Not inspected",
        status: "unknown",
        intent: "neutral",
      },
      {
        label: "Behavior applied",
        value: "Not inspected",
        status: "unknown",
        intent: "neutral",
      },
      {
        label: "Route/value",
        value: "Not inspected",
        status: "unknown",
        intent: "neutral",
      },
      {
        label: "Local verifier",
        value: "Not inspected",
        status: "unknown",
        intent: "neutral",
      },
    ];
  }
  if (!data) {
    return [
      {
        label: "Claim",
        value: "Checking product evidence",
        status: "checking",
        intent: "neutral",
      },
    ];
  }
  const summary = data.claimSummary;
  return [
    summaryItemRow(summary.claim),
    summaryItemRow(summary.learned),
    summaryItemRow(summary.handoff),
    summaryItemRow(summary.application),
    summaryItemRow(summary.routeAction),
    summaryItemRow(summary.localVerifier),
    summaryItemRow(summary.value),
    summaryItemRow(summary.excludedEvidence),
  ];
}

function phaseIntent(state: AgentImprovementDashboardResponse["activeLoopEvidence"]["state"]): "success" | "info" | "warning" | "neutral" {
  if (state === "verified") return "success";
  if (state === "observed") return "info";
  if (state === "suppressed") return "warning";
  return "neutral";
}

function stageIntent(state: AgentImprovementDashboardResponse["activeLoopEvidence"]["stages"]["before"]): "success" | "info" | "warning" | "neutral" {
  if (state === "verified") return "success";
  if (state === "observed") return "info";
  if (state === "suppressed") return "warning";
  return "neutral";
}

function overviewActiveLoopRows(data: AgentImprovementDashboardResponse | undefined, error?: unknown): Array<{
  label: string;
  value: string;
  status: string;
  intent: "success" | "info" | "warning" | "neutral";
}> {
  if (error && !data) {
    return [
      { label: "Loop phase", value: "Not inspected", status: "unavailable", intent: "warning" },
      { label: "Guardrail", value: "Not inspected", status: "unknown", intent: "neutral" },
      { label: "Before / after", value: "Not inspected", status: "unknown", intent: "neutral" },
      { label: "Recovery", value: "Not inspected", status: "unknown", intent: "neutral" },
    ];
  }
  if (!data) {
    return [
      { label: "Loop phase", value: "Checking product evidence", status: "checking", intent: "neutral" },
    ];
  }
  const evidence = data.activeLoopEvidence;
  const guardrail = data.guardrailDecisions[0] ?? null;
  const beforeCount = evidence.artifacts.filter((artifact) => artifact.kind === "before").length;
  const afterCount = evidence.artifacts.filter((artifact) => artifact.kind === "after").length;
  const counterevidenceCount = data.learningSummaries.reduce(
    (count, learning) => count + (learning.counterevidenceRefs?.length ?? 0),
    0,
  );
  const recovery = data.learningSummaries.find((learning) => learning.wrongRecommendationRecovery)?.wrongRecommendationRecovery;
  const recoveryVisible = counterevidenceCount > 0 || evidence.transferExposureIds.length > 0;
  return [
    {
      label: "Loop phase",
      value: evidence.activePhase.replace(/_/g, " "),
      status: evidence.state,
      intent: phaseIntent(evidence.state),
    },
    {
      label: "Guardrail",
      value: guardrail ? `${guardrail.guardName.replace(/_/g, " ")}: ${guardrail.decision.replace(/_/g, " ")}` : "No decision visible",
      status: guardrail?.reasonCode.replace(/_/g, " ") ?? "missing",
      intent: guardrail ? (guardrail.decision === "allow" ? "success" : "warning") : "neutral",
    },
    {
      label: "Before / after",
      value: `${beforeCount} before / ${afterCount} after`,
      status: `${evidence.stages.before} / ${evidence.stages.after}`,
      intent: evidence.stages.before === "verified" && evidence.stages.after === "verified"
        ? "success"
        : stageIntent(evidence.stages.before === "missing" ? evidence.stages.after : evidence.stages.before),
    },
    {
      label: "Recovery",
      value: recoveryVisible
        ? `${counterevidenceCount} counterevidence / ${evidence.transferExposureIds.length} transfer`
        : "No recovery packet",
      status: recovery ? recovery.selectorDecision : recoveryVisible ? evidence.stages.transfer : "not proven",
      intent: recoveryVisible ? stageIntent(evidence.stages.transfer) : "neutral",
    },
  ];
}

function evidenceState(data: AgentImprovementDashboardResponse | undefined, error?: unknown): {
  label: string;
  intent: "success" | "info" | "warning" | "neutral";
  detail: string;
} {
  if (error && !data) {
    return {
      label: "unavailable",
      intent: "warning",
      detail: "The agent-improvement endpoint is unavailable, so this page cannot inspect Codex integration, learning delivery, or outcome evidence.",
    };
  }
  if (!data) {
    return {
      label: "checking",
      intent: "neutral",
      detail: "Excellent is checking whether this workspace has scoped agent-improvement evidence.",
    };
  }
  const connection = data.agentConnections[0];
  if (connection?.state === "connected") {
    return {
      label: "connected",
      intent: "success",
      detail: "A live reporting session is available and the dashboard can inspect delivery evidence.",
    };
  }
  if (connection?.state === "observed") {
    return {
      label: "observed",
      intent: "info",
      detail: "Stored exposure evidence exists for this agent, but no current live reporting session is available.",
    };
  }
  if (connection?.state === "configured") {
    return {
      label: "configured",
      intent: "info",
      detail: "An integration is configured, but no current live reporting session is available.",
    };
  }
  if (connection?.state === "needs_setup") {
    return {
      label: "needs setup",
      intent: "neutral",
      detail: "No scoped integration, live report, or learning exposure has been recorded for this agent yet.",
    };
  }
  if (connection?.state === "unknown") {
    return {
      label: "unknown",
      intent: "warning",
      detail: connection.reason,
    };
  }
  return {
    label: "no agent row",
    intent: "warning",
    detail: "The endpoint returned no selected-agent evidence row for this workspace.",
  };
}

function errorValue(error: unknown): string {
  if (error instanceof Error) return error.message;
  return String(error);
}

function selectedConnection(data: AgentImprovementDashboardResponse | undefined) {
  const actorId = data?.provenance.actorId ?? "";
  const shortActorId = actorId.replace(/^[^:]+:/, "");
  return data?.agentConnections.find((connection) => connection.agentId === actorId || connection.agentId === shortActorId)
    ?? data?.agentConnections.find((connection) => connection.state === "connected" && !connection.agentId.startsWith("daemon-"))
    ?? data?.agentConnections[0];
}

export function AgentImprovementOverviewCard({
  data,
  fallbackActorId = "real:agent_codex",
  fallbackEnvironment = "real",
  isLoading = false,
  error,
}: AgentImprovementOverviewCardProps) {
  const state = evidenceState(data, error);
  const actorId = data?.provenance.actorId ?? fallbackActorId;
  const connection = selectedConnection(data);
  const environment = data?.provenance.environment ?? fallbackEnvironment;
  const evidenceHref = environment === "real" ? "/?env=real" : `/?env=${environment}`;
  const pathRows = overviewEvidencePathRows(data, error);
  const activeLoopRows = overviewActiveLoopRows(data, error);
  const loopStoryRows = overviewLoopStoryRows(data, error);
  const unavailable = Boolean(error && !data);
  const summary = data?.claimSummary;

  return (
    <Card className="w-full max-w-[calc(100vw-2rem)] overflow-hidden sm:max-w-full">
      <CardHeader className="gap-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 type-subtext font-medium uppercase tracking-normal text-muted-foreground">
              <Icon name="sparkles" size="sm" />
              Agent improvement evidence
            </div>
            <CardTitle className="mt-2 type-title">What is Excellent proving about this agent?</CardTitle>
            <CardDescription className="mt-1 max-w-3xl break-words [overflow-wrap:anywhere]">
              {state.detail}
            </CardDescription>
          </div>
          <IntentBadge intent={state.intent}>{state.label}</IntentBadge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4 border-t border-border pt-4">
        {unavailable ? (
          <div className="rounded border border-destructive/30 bg-destructive/5 p-3">
            <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
              <div className="min-w-0">
                <div className="type-body font-medium text-foreground">Evidence inspection stopped</div>
                <div className="mt-1 break-words font-mono type-subtext text-muted-foreground">
                  /api/verification/agent-improvement
                </div>
              </div>
              <IntentBadge intent="warning">api unavailable</IntentBadge>
            </div>
            <p className="mt-2 type-subtext leading-5 text-muted-foreground">
              The application shell is running, but Excellent could not inspect agent evidence. Restore the API/vault connection, then reload this card before treating setup, delivery, routing, or savings as known.
            </p>
            <p className="mt-2 break-words type-subtext text-muted-foreground">
              Error: {errorValue(error)}
            </p>
            <div className="mt-3 grid gap-2 sm:grid-cols-3">
              <div className="rounded border border-destructive/20 bg-background/70 p-2">
                <div className="type-subtext font-medium text-foreground">Restore evidence API</div>
                <div className="mt-1 break-words font-mono type-subtext text-muted-foreground">EXCELLENT_DB_KEY / vault access</div>
              </div>
              <div className="rounded border border-destructive/20 bg-background/70 p-2">
                <div className="type-subtext font-medium text-foreground">Arm local verifier</div>
                <div className="mt-1 break-words font-mono type-subtext text-muted-foreground">ollama/qwen2.5-coder:7b</div>
              </div>
              <div className="rounded border border-destructive/20 bg-background/70 p-2">
                <div className="type-subtext font-medium text-foreground">Inspect evidence route</div>
                <div className="mt-1 break-words font-mono type-subtext text-muted-foreground">{evidenceHref}</div>
              </div>
            </div>
          </div>
        ) : null}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="root-loop-story">
          {loopStoryRows.map((item) => (
            <div key={item.label} className="min-w-0 overflow-hidden rounded border border-border/60 p-3">
              <div className="flex min-w-0 flex-col items-start gap-2">
                <div className="flex w-full min-w-0 items-start justify-between gap-2">
                  <div className="type-subtext text-muted-foreground">{item.label}</div>
                  <IntentBadge className="shrink-0" intent={item.intent}>{item.status}</IntentBadge>
                </div>
                <div className="min-w-0 w-full break-words type-body font-medium text-foreground [overflow-wrap:anywhere]">{item.value}</div>
                {item.detail ? (
                  <p className="min-w-0 w-full break-words type-subtext leading-5 text-muted-foreground [overflow-wrap:anywhere]">
                    {item.detail}
                  </p>
                ) : null}
              </div>
            </div>
          ))}
        </div>
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          <div className="min-w-0 overflow-hidden">
            <div className="type-subtext text-muted-foreground">Claim</div>
            <div className="mt-1 flex min-w-0 flex-wrap items-start justify-between gap-2">
              <div className="min-w-0 break-words type-body font-medium text-foreground [overflow-wrap:anywhere]">
                {unavailable ? "Evidence endpoint unavailable" : isLoading && !data && !error ? "Loading..." : summary?.claim.value ?? "Checking product evidence"}
              </div>
              {summary?.claim.state ? (
                <span className="max-w-full shrink-0">
                  <IntentBadge intent={intentForState(summary.claim.state)}>
                    {statusForState(summary.claim.state)}
                  </IntentBadge>
                </span>
              ) : null}
            </div>
          </div>
          <div className="min-w-0 overflow-hidden">
            <div className="type-subtext text-muted-foreground">Agent</div>
            <div className="mt-1 break-all type-body font-medium text-foreground">{connection?.agentId ?? actorId}</div>
          </div>
          <div className="min-w-0 overflow-hidden">
            <div className="type-subtext text-muted-foreground">Learning</div>
            <div className="mt-1 break-all type-body font-medium text-foreground">
              {unavailable ? "Not inspected" : summary?.learned.value ?? "Checking"}
            </div>
          </div>
          <div className="min-w-0 overflow-hidden">
            <div className="type-subtext text-muted-foreground">Route to use</div>
            <div className="mt-1 break-words type-body font-medium text-foreground [overflow-wrap:anywhere]">
              {unavailable ? "Not inspected" : summary?.routeAction.value ?? "Checking"}
            </div>
          </div>
          <div className="min-w-0 overflow-hidden">
            <div className="type-subtext text-muted-foreground">Cost/value</div>
            <div className="mt-1 break-words type-body font-medium text-foreground [overflow-wrap:anywhere]">
              {unavailable ? "Not inspected" : summary?.value.value ?? "Checking"}
            </div>
          </div>
        </div>
        <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4" data-testid="root-active-loop-evidence">
          {activeLoopRows.map((item) => (
            <div key={item.label} className="min-w-0 overflow-hidden rounded border border-border/60 p-3">
              <div className="flex min-w-0 flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="type-subtext text-muted-foreground">{item.label}</div>
                  <div className="mt-1 break-words type-body font-medium text-foreground [overflow-wrap:anywhere]">{item.value}</div>
                </div>
                <span className="max-w-full shrink-0"><IntentBadge intent={item.intent}>{item.status}</IntentBadge></span>
              </div>
            </div>
          ))}
        </div>
        <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
          {pathRows.map((item) => (
            <div key={item.label} className="min-w-0 overflow-hidden rounded border border-border/60 p-3">
              <div className="flex min-w-0 flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="type-subtext text-muted-foreground">{item.label}</div>
                  <div className="mt-1 break-words type-body font-medium text-foreground [overflow-wrap:anywhere]">{item.value}</div>
                  {item.detail ? (
                    <p className="mt-2 line-clamp-3 break-words type-subtext leading-5 text-muted-foreground [overflow-wrap:anywhere]">
                      {item.detail}
                    </p>
                  ) : null}
                </div>
                <span className="max-w-full shrink-0"><IntentBadge intent={item.intent}>{item.status}</IntentBadge></span>
              </div>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 lg:justify-end">
          {error ? (
            <span className="inline-flex min-h-9 items-center rounded-md border border-destructive/40 px-3 type-subtext text-destructive">
              Evidence endpoint unavailable
            </span>
          ) : null}
          <Link
            href={evidenceHref}
            className="inline-flex min-h-9 items-center justify-center gap-2 rounded-md bg-primary px-3 type-body font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            <Icon name="arrow-right" size="sm" />
            Open evidence
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}
